import React, { useRef, useState } from 'react';
import { PlayIcon } from 'lucide-react';
import { SectionLabel } from './ui/SectionLabel';
import { CtaButton } from './ui/CtaButton';
const VIDEO = 'https://cdn.magicpatterns.com/uploads/mvXy53TuyXw4DVMVBhtE6e/para landing (2).mp4';
const labels = ['Class Intention', 'Duration', 'Level', 'Props', 'Opening', 'Warm-Up', 'Main Sequence', 'Teaching Cues', 'Transitions', 'Modifications', 'Cooldown', 'Closing'];
const stats = [{
  value: '120',
  label: 'Complete classes'
}, {
  value: '6',
  label: 'Professional volumes'
}, {
  value: '20',
  label: 'Classes per volume'
}, {
  value: '40+',
  label: 'Weeks of material*'
}];
export function ClassesVideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const handleMetadata = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;
    setRatio(video.videoWidth / video.videoHeight);
    video.playbackRate = 0.7;
    void video.play().catch(() => undefined);
  };
  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => undefined);
      setPaused(false);
    } else {
      video.pause();
      setPaused(true);
    }
  };
  return <section className="w-full bg-sand py-10 sm:py-14 lg:py-20" aria-labelledby="classes-heading" data-id="element-17" data-mp="3bgijcbfw5pk8">
      <div className="mx-auto w-full max-w-content px-5 sm:px-8" data-id="element-18" data-mp="3gek18t1kziu4">
        <div className="mx-auto max-w-2xl text-center" data-id="element-19" data-mp="3gcpgbeqb7qq1">
          <div className="flex justify-center" data-id="element-20" data-mp="3l6trka6lykwx">
            <SectionLabel data-id="element-21" data-mp="3l84u66obhmmg">Inside the system</SectionLabel>
          </div>
          <h2 id="classes-heading" className="mt-3 font-serif text-[1.6rem] font-semibold uppercase leading-[1.08] text-ink sm:text-4xl" data-id="element-22" data-mp="3q43lo34xzkr3">
            120 classes.
            <span className="block text-forest" data-id="element-23" data-mp="30fcu90im6xh">Here’s what that actually means.</span>
          </h2>
          <p className="mx-auto mt-3 max-w-md text-[0.9rem] leading-snug text-muted sm:text-lg" data-id="element-24" data-mp="2011mow19r0s3">
            Not 120 pose lists. Not 120 unfinished ideas.
            <span className="mt-1 block font-semibold text-ink" data-id="element-25" data-mp="28tdccfok6k70">
              120 complete classes built to be taught.
            </span>
          </p>
        </div>

        <div className="mx-auto mt-5 w-full max-w-xs overflow-hidden rounded-2xl border border-sandDark bg-ivory shadow-card sm:max-w-md sm:rounded-3xl lg:max-w-lg" style={ratio ? {
        aspectRatio: String(ratio)
      } : undefined} data-id="element-26" data-mp="240kb41sattzo">
          <button type="button" onClick={togglePlayback} aria-label={paused ? 'Play the product video' : 'Pause the product video'} className="group relative block h-full w-full focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold" data-id="element-27" data-mp="2rqodk5tr3np3">
            <video ref={videoRef} className="block h-full w-full object-cover" src={VIDEO} autoPlay muted loop playsInline preload="metadata" disablePictureInPicture onLoadedMetadata={handleMetadata} data-id="element-28" data-mp="j8enk73y0zc5" />
            <span className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out ${paused ? 'opacity-100' : 'opacity-0'}`} data-id="element-29" data-mp="egeqqnj66mjs">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ivory/90 shadow-card" data-id="element-30" data-mp="3mp4xuy6k5yjn">
                <PlayIcon className="ml-0.5 h-5 w-5 text-forest" aria-hidden="true" data-id="element-31" data-mp="3rnya6na3zji8" />
              </span>
            </span>
          </button>
        </div>

        <p className="mt-2 text-center text-[0.55rem] uppercase tracking-label text-muted" data-id="element-32" data-mp="1kyjppn9xbufg">
          {paused ? 'Tap to resume' : 'Tap the video to pause'}
        </p>

        <p className="mt-5 text-center font-serif text-[1rem] font-semibold uppercase leading-tight text-ink sm:text-xl" data-id="element-33" data-mp="3lyydz3mr5onc">
          Every class gives you the structure — not just the idea.
        </p>

        <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" data-id="element-34" data-mp="3c0sgqzbw56ot">
          {labels.map(label => <li key={label} className="rounded-xl border border-sandDark bg-ivory px-3 py-2.5 text-center text-[0.65rem] font-semibold uppercase leading-tight tracking-label text-ink sm:text-[0.7rem]" data-id="element-35" data-mp="37zp22g5kxgp6">
              {label}
            </li>)}
        </ul>

        <ul className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-id="element-36" data-mp="2tblr0scojeti">
          {stats.map(({
          value,
          label
        }) => <li key={label} className="rounded-2xl border border-gold/30 bg-ivory px-3 py-4 text-center" data-id="element-37" data-mp="2oaeksxq6qnoy">
              <p className="font-serif text-3xl font-semibold leading-none text-forest sm:text-4xl" data-id="element-38" data-mp="2fcm4prcn3c0i">
                {value}
              </p>
              <p className="mt-1.5 text-[0.58rem] font-semibold uppercase tracking-label text-muted" data-id="element-39" data-mp="uvmra445h3sk">
                {label}
              </p>
            </li>)}
        </ul>

        <p className="mt-3 text-center text-[0.6rem] leading-relaxed text-muted" data-id="element-40" data-mp="13uzy0p0w3e9l">
          *Based on teaching approximately 3 classes per week.
        </p>

        <p className="mx-auto mt-5 max-w-xl text-center font-serif text-[1rem] font-semibold uppercase leading-tight text-forest sm:text-xl" data-id="element-41" data-mp="c3tv60tsb2gm">
          Months of teaching material without starting every class from zero.
        </p>

        <div className="mt-6" data-id="element-42" data-mp="l25x2z03pmod">
          <CtaButton label="Get instant access" microcopy="One-Time Purchase • No Subscription • Instant Access" data-id="element-43" data-mp="puffzv2stmh5" />
        </div>
      </div>
    </section>;
}